import { Capacitor } from "@capacitor/core";
import { Dexie as DexieType } from "dexie";
import { ConnectionStatus } from "@capacitor/network";
import { Device } from "@capacitor/device";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export class LocalLogger {
  #network: ConnectionStatus | undefined;
  #platform: string;

  #idb: DexieType | undefined;

  private writeQueue: Promise<void> = Promise.resolve();
  private readonly fileName: string = "fabriq.log";
  private fileExists = false;
  private readonly maxEntries: number = 25_000;

  constructor() {
    this.#platform = Capacitor.getPlatform();
    this.log(`LocalLogger Initialized on ${this.#platform}`);
  }

  async deleteLogs(): Promise<void> {
    const fileExists = await this.checkIfFileExists();
    if (!fileExists) return;
    await Filesystem.deleteFile({
      path: this.fileName,
      directory: Directory.Data,
    });
  }

  async truncateLogsToLatest(): Promise<void> {
    const fileExists = await this.checkIfFileExists();
    if (!fileExists) return;
    const { data } = await Filesystem.readFile({
      path: this.fileName,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    const content = data instanceof Blob ? await data.text() : data;
    const entries = content.split("\n");
    const entriesToRemove = entries.length - this.maxEntries;
    if (entriesToRemove <= 0) return;

    const lastEntries = entries.slice(entriesToRemove);
    await Filesystem.writeFile({
      path: this.fileName,
      directory: Directory.Data,
      data: lastEntries.join("\n"),
      encoding: Encoding.UTF8,
    });
  }

  async logDevice(appVersion: string): Promise<void> {
    const device = await Device.getInfo();
    this.log(`Device Info: ${JSON.stringify(device)}`);
    this.log(`Version Info: ${appVersion}`);
  }

  async logPush(message: Record<string, any>) {
    const now = new Date();
    const logMessage = `${now.toISOString()} - [PUSH] - ${JSON.stringify(
      message
    )} ${
      this.#network
        ? `(${
            this.#network.connected
              ? this.#network.connectionType
              : "not connected"
          })`
        : ""
    }\n`;
    const currentTask = this.writeQueue.then(async () => {
      await this.#performWrite(logMessage);
    });

    this.writeQueue = currentTask.catch((error) => {
      console.error("Failed to write log:", error);
    });

    return currentTask;
  }

  async log(message: string) {
    const now = new Date();
    const logMessage = `${now.toISOString()} - ${message} ${
      this.#network
        ? `(${
            this.#network.connected
              ? this.#network.connectionType
              : "not connected"
          })`
        : ""
    }\n`;
    console.log("LOCALLOGGER: " + logMessage);
    const currentTask = this.writeQueue.then(async () => {
      // Actual filesystem write operation
      await this.#performWrite(logMessage);
    });

    this.writeQueue = currentTask.catch((error) => {
      console.error("Failed to write log:", error);
    });

    return currentTask;
  }

  async setNetwork(network: ConnectionStatus) {
    this.#network = network;
  }

  async checkIfFileExists(): Promise<boolean> {
    try {
      await Filesystem.stat({
        path: this.fileName,
        directory: Directory.Data,
      });
      return true;
    } catch {
      return false;
    }
  }

  async #performWrite(message: string) {
    if (!this.fileExists) {
      const fileExists = await this.checkIfFileExists();
      if (fileExists) {
        this.fileExists = true;
      } else {
        await Filesystem.writeFile({
          path: this.fileName,
          directory: Directory.Data,
          data: "",
          encoding: Encoding.UTF8,
        });
        this.fileExists = true;
      }
    }
    await Filesystem.appendFile({
      path: this.fileName,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
      data: "\n" + message,
    });
  }

  async getLogs(): Promise<string> {
    const fileExists = this.fileExists ? true : await this.checkIfFileExists();
    if (!fileExists) return "";
    const { data } = await Filesystem.readFile({
      path: this.fileName,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    return data instanceof Blob ? await data.text() : data;
  }

  async downloadLogs() {
    if (this.#platform === "web") {
      await this.#downloadForWeb();
    } else {
      await this.#downloadForMobile();
    }
  }

  async #downloadForMobile() {
    const logFileContent = await this.getLogs();
    await Filesystem.writeFile({
      path: "fabriq.log",
      data: logFileContent,
      directory: Directory.Cache,
      encoding: Encoding.UTF8,
    });
    const { uri } = await Filesystem.getUri({
      path: "fabriq.log",
      directory: Directory.Cache,
    });
    await Share.share({
      title: "Fabriq Log File",
      text: "Here is the log file",
      url: uri,
      dialogTitle: "Share log file",
    });
  }

  async #downloadForWeb() {
    const logFileContent = await this.getLogs();
    console.log(logFileContent);
    const blob = new Blob([logFileContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fabriq.log";
    a.click();
    URL.revokeObjectURL(url);
  }
}
